import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../core/services/auth.service';

@Component({
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="page">

      <!-- ── Brand panel ── -->
      <aside class="brand-panel">
        <div class="brand-glow brand-glow--tl"></div>
        <div class="brand-glow brand-glow--br"></div>

        <div class="brand-content">
          <img src="/jci-nusantara-logo.png" alt="JCI Nusantara" class="brand-logo"
               (error)="logoFailed = true" *ngIf="!logoFailed" />
          <div class="brand-logo-fallback" *ngIf="logoFailed">JCI Nusantara</div>

          <div class="brand-text">
            <h2 class="brand-title">Your chapter,<br>all in one place.</h2>
            <p class="brand-sub">Run events, track members, and manage chapter operations with ease — built for JCI officers like you.</p>
          </div>

          <div class="brand-badges">
            <span class="badge">Events</span>
            <span class="badge">Members</span>
            <span class="badge">Sales</span>
            <span class="badge">Organisation</span>
          </div>
        </div>

        <p class="brand-footer">JCI Nusantara &mdash; Junior Chamber International</p>
      </aside>

      <!-- ── Login panel ── -->
      <main class="login-panel">
        <div class="login-card">

          <div class="login-header">
            <div class="login-icon">
              <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 17l5-5-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15 12H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div>
              <p class="login-eyebrow">JCI Nusantara</p>
              <h1 class="login-title">Sign in</h1>
            </div>
          </div>

          <p class="login-desc">Welcome back! Sign in with your JCI Nusantara account to get started.</p>

          <div class="feedback error" *ngIf="error">
            <svg viewBox="0 0 24 24" fill="none" width="15" height="15" style="flex-shrink:0">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            {{ error }}
          </div>

          <button class="btn-primary" type="button" [disabled]="loading" (click)="signIn()">
            <span *ngIf="!loading">Continue with Auth0</span>
            <span class="loading-row" *ngIf="loading">
              <span class="spinner"></span>
              Redirecting…
            </span>
          </button>

          <p class="login-hint">Access is limited to active JCI Nusantara officers.</p>
        </div>
      </main>

    </div>
  `,
  styles: [`
    /* ── Layout ── */
    .page {
      min-height: 100vh;
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    /* ── Brand panel ── */
    .brand-panel {
      position: relative;
      background: #130f2d;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 3rem 3.5rem;
      overflow: hidden;
    }

    .brand-glow {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
    }
    .brand-glow--tl {
      width: 480px; height: 480px;
      top: -140px; left: -140px;
      background: radial-gradient(circle, rgba(0,151,215,0.18) 0%, transparent 70%);
    }
    .brand-glow--br {
      width: 360px; height: 360px;
      bottom: -100px; right: -100px;
      background: radial-gradient(circle, rgba(87,188,188,0.14) 0%, transparent 70%);
    }

    .brand-content {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 2.5rem;
      flex: 1;
      justify-content: center;
    }

    .brand-logo {
      height: 160px;
      width: 160px;
      object-fit: contain;
      display: block;
    }
    .brand-logo-fallback {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 1.6rem;
      font-weight: 800;
      color: #0097d7;
      letter-spacing: 0.04em;
    }

    .brand-text {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .brand-title {
      margin: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 2.4rem;
      font-weight: 800;
      color: #ffffff;
      line-height: 1.15;
      letter-spacing: -0.02em;
    }
    .brand-sub {
      margin: 0;
      font-size: 1rem;
      color: rgba(255,255,255,0.5);
      line-height: 1.6;
      max-width: 340px;
    }

    .brand-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    .badge {
      display: inline-flex;
      align-items: center;
      padding: 0.3rem 0.8rem;
      border-radius: 100px;
      border: 1px solid rgba(0,151,215,0.35);
      background: rgba(0,151,215,0.1);
      color: #60c8ef;
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.02em;
    }

    .brand-footer {
      position: relative;
      z-index: 1;
      margin: 0;
      font-size: 0.78rem;
      color: rgba(255,255,255,0.22);
      letter-spacing: 0.03em;
    }

    /* ── Login panel ── */
    .login-panel {
      background: #f8f8fa;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2.5rem 2rem;
    }

    .login-card {
      width: min(400px, 100%);
      display: flex;
      flex-direction: column;
      gap: 1.4rem;
    }

    .login-header {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .login-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: #130f2d;
      color: #0097d7;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .login-eyebrow {
      margin: 0 0 2px;
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #0097d7;
    }
    .login-title {
      margin: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 1.75rem;
      font-weight: 800;
      color: #130f2d;
      line-height: 1.1;
    }

    .login-desc {
      margin: 0;
      font-size: 0.92rem;
      color: #717182;
      line-height: 1.55;
    }

    /* Error feedback */
    .feedback.error {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.7rem 0.9rem;
      border-radius: 8px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #991b1b;
      font-size: 0.88rem;
      line-height: 1.45;
    }

    /* CTA button */
    .btn-primary {
      width: 100%;
      padding: 0.85rem 1.2rem;
      border-radius: 10px;
      border: none;
      background: #0097d7;
      color: #fff;
      font-size: 0.95rem;
      font-weight: 700;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms, box-shadow 150ms, transform 100ms;
      box-shadow: 0 4px 14px rgba(0,151,215,0.3);

      &:hover:not(:disabled) {
        background: #0086bf;
        box-shadow: 0 6px 20px rgba(0,151,215,0.38);
        transform: translateY(-1px);
      }
      &:active:not(:disabled) {
        transform: translateY(0);
      }
      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
    }

    .loading-row {
      display: inline-flex;
      align-items: center;
      gap: 0.55rem;
    }
    .spinner {
      width: 15px;
      height: 15px;
      border: 2.5px solid rgba(255,255,255,0.35);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .login-hint {
      margin: 0;
      font-size: 0.78rem;
      color: #b0b0bb;
      text-align: center;
      line-height: 1.5;
    }

    /* ── Mobile ── */
    @media (max-width: 768px) {
      .page {
        grid-template-columns: 1fr;
      }
      .brand-panel {
        padding: 2.5rem 1.75rem 2rem;
        min-height: 0;
      }
      .brand-title { font-size: 1.75rem; }
      .brand-content { gap: 1.5rem; }
      .login-panel {
        padding: 2.5rem 1.25rem 3rem;
      }
    }
  `]
})
export class LoginComponent {
  loading = false;
  error = '';
  logoFailed = false;

  constructor(
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute
  ) {}

  async signIn(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      const rawNext = this.route.snapshot.queryParamMap.get('next') || '/dashboard';
      const nextPath = this.auth.getSafeNextPath(rawNext);
      await this.auth.startSsoLogin(nextPath);
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'SSO login failed';
      this.loading = false;
    }
  }
}
